import React, { useCallback, useEffect, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle, RefreshCw, Wrench, PlayCircle } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useLatestRequest } from '../hooks/useLatestRequest';

export const COMPLETENESS_STYLES = {
  VERIFIED: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  INCOMPLETE: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  MISMATCH: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  STALE: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  UNAVAILABLE: 'bg-zinc-800 text-zinc-300 border-zinc-600',
};

const inr = (v) => (v === null || v === undefined ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

const Metric = ({ label, source, local, diff, testid }) => (
  <div className="rounded-md border border-zinc-800 p-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
    <div className="text-xs text-zinc-300 font-mono">Source {source}</div>
    <div className="text-xs text-zinc-300 font-mono">Accounts {local}</div>
    <div className={`text-xs font-mono ${diff && diff !== '0' && diff !== '₹0' ? 'text-rose-300' : 'text-emerald-300'}`}>Δ {diff}</div>
  </div>
);

function JobRow({ job, canRepair, onResume }) {
  const pending = job.days_pending?.length || 0;
  const done = job.days_total - pending;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs py-1.5" data-testid={`sales-repair-job-${job.job_id}`}>
      <span className="font-mono text-zinc-400">{job.date_from} → {job.date_to}</span>
      <Badge className="text-[10px] border bg-zinc-800 border-zinc-700 text-zinc-200">{job.state}</Badge>
      <span className="text-zinc-500">{done}/{job.days_total} days</span>
      {job.summary && <span className="text-zinc-500">{Object.entries(job.summary).map(([k, v]) => `${k} ${v}`).join(' · ')}</span>}
      {canRepair && ['INTERRUPTED', 'COMPLETED_WITH_ISSUES', 'FAILED'].includes(job.state) && (
        <Button size="sm" variant="outline" className="h-6 text-[11px] ml-auto" onClick={() => onResume(job.job_id)}
          data-testid={`sales-repair-resume-${job.job_id}`}>
          <PlayCircle className="w-3 h-3 mr-1" /> Resume
        </Button>
      )}
    </div>
  );
}

export default function SalesCompletenessPanel({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const perms = usePermissions();
  const begin = useLatestRequest();

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    const isLatest = begin();
    setLoading(true);
    try {
      const [c, j] = await Promise.all([
        accountsAPI.getSalesCompleteness({ date_from: dateFrom, date_to: dateTo }),
        accountsAPI.listSalesRepairJobs(),
      ]);
      if (!isLatest()) return;
      setData(c.data);
      setJobs(j.data?.jobs || []);
    } catch (e) {
      if (isLatest()) setData({ status: 'UNAVAILABLE', reason: 'Completeness check failed to load', comparison_performed: false });
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [dateFrom, dateTo, begin]);

  useEffect(() => { load(); }, [load]);

  const repair = async () => {
    setBusy(true);
    try {
      await accountsAPI.repairSalesProjection({ date_from: dateFrom, date_to: dateTo });
      toast.success('Repair started — days are re-synced from Sales Capture one by one');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.code === 'REPAIR_ALREADY_RUNNING' ? 'A repair is already running' : d?.message || 'Repair failed');
    } finally { setBusy(false); }
  };
  const resume = async (id) => {
    try { await accountsAPI.resumeSalesRepairJob(id); toast.success('Repair resumed'); load(); }
    catch (e) { toast.error('Could not resume'); }
  };

  const status = data?.status || 'UNAVAILABLE';
  const d = data?.difference || {};
  const canRepair = perms.financial;
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sales-completeness-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400" /> Sales completeness vs Sales Capture
        </CardTitle>
        <div className="flex items-center gap-2">
          {!loading && <Badge className={`text-[10px] border ${COMPLETENESS_STYLES[status]}`} data-testid="sales-completeness-status">{status}</Badge>}
          <Button size="sm" variant="ghost" className="h-7" onClick={load} data-testid="sales-completeness-refresh"><RefreshCw className="w-3 h-3" /></Button>
          {canRepair && status !== 'VERIFIED' && (
            <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-black" disabled={busy || loading}
              onClick={repair} data-testid="sales-completeness-repair-btn">
              <Wrench className="w-3 h-3 mr-1" /> Repair this period
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {loading ? <Skeleton className="h-20" /> : (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-zinc-400" data-testid="sales-completeness-meta">
              <span>Requested {data?.requested_period?.date_from} → {data?.requested_period?.date_to}</span>
              <span>Covered {data?.covered_period?.date_from} → {data?.covered_period?.date_to}</span>
              <span>Comparison performed: <b className="text-zinc-200">{data?.comparison_performed ? 'Yes' : 'No'}</b></span>
              <span>Source: <b className="text-zinc-200">{data?.source_available ? 'available' : 'unavailable'}</b></span>
              <span>Last successful sync: <b className="text-zinc-200">{data?.last_successful_sync ? new Date(data.last_successful_sync).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'never'}</b></span>
            </div>
            {data?.reason && <div className="text-amber-300 flex items-center gap-1" data-testid="sales-completeness-reason"><AlertTriangle className="w-3 h-3" />{data.reason}</div>}
            {data?.comparison_performed && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Metric testid="sc-metric-count" label="Transactions" source={data.source.count} local={data.projection.count} diff={String(d.count)} />
                {['net_sales', 'final_total', 'taxes_total', 'paid_amount'].map((f) => (
                  <Metric key={f} testid={`sc-metric-${f}`} label={f.replace('_', ' ')} source={inr(data.source[f])} local={inr(data.projection[f])} diff={inr(d[f])} />
                ))}
              </div>
            )}
            {data?.problem_days?.length > 0 && (
              <div data-testid="sales-completeness-problem-days">
                <div className="text-zinc-400 mb-1">{data.problem_days.length} day(s) differ from the source:</div>
                <div className="flex flex-wrap gap-1">
                  {data.problem_days.map((p) => (
                    <span key={p.business_date} className="px-1.5 py-0.5 rounded border border-rose-500/30 text-rose-300 font-mono text-[10px]"
                      title={`source ${p.source_count} · accounts ${p.local_count}`}>
                      {p.business_date} ({p.local_count}/{p.source_count})
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-zinc-500">Refunds and voids: not exposed by the Sales Capture contract — not verified. {data?.rules?.[status]}</p>
            {jobs.length > 0 && (
              <div className="border-t border-zinc-800 pt-2" data-testid="sales-repair-jobs">
                <div className="text-zinc-400 mb-1">Repair jobs</div>
                {jobs.slice(0, 5).map((j) => <JobRow key={j.job_id} job={j} canRepair={canRepair} onResume={resume} />)}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
